import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaUndo,
  FaUpload
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import ServiceHeroExtrasEditor from '../../components/cms/ServiceHeroExtrasEditor';

const ZohoAdvisoryCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    heroTitle: '',
    heroBody: '',
    heroImage: '',
    heroBtn1Text: '',
    heroBtn2Text: '',

    heroBadgeText: '',
    heroBadgeIcon: 'check',
    heroTrustBadges: [],

    servicesTitle: '',
    servicesSubtitle: '',
    services: [],

    whyAdvisoryTitle: '',
    whyAdvisoryBody: '',

    dashboardTitle: '',
    dashboardSubtitle: '',
    dashboardBody: '',
    dashboardBtnText: '',
    dashboardBulletsTitle: '',
    dashboardBullets: [],

    stepsTitle: '',
    stepsSubtitle: '',
    stepsBody: '',
    stepsBtnText: '',
    stepsBullets: [],
    processSteps: [],

    beneficiariesTitle: '',
    beneficiariesSubtitle: '',
    beneficiaries: [],

    whyChooseTitle: '',
    whyChooseSubtitle: '',
    whyChoose: [],

    faqsTitle: '',
    faqsSubtitle: '',
    faqs: [],

    isPublished: true
  });

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/zoho-advisory/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data || {};
        setForm({
          heroTitle: data.heroTitle || '',
          heroBody: data.heroBody || '',
          heroImage: data.heroImage || '',
          heroBtn1Text: data.heroBtn1Text || '',
          heroBtn2Text: data.heroBtn2Text || '',

          heroBadgeText: data.heroBadgeText || '',
          heroBadgeIcon: data.heroBadgeIcon || 'check',
          heroTrustBadges: data.heroTrustBadges || [],

          servicesTitle: data.servicesTitle || '',
          servicesSubtitle: data.servicesSubtitle || '',
          services: data.services || [],

          whyAdvisoryTitle: data.whyAdvisoryTitle || '',
          whyAdvisoryBody: data.whyAdvisoryBody || '',

          dashboardTitle: data.dashboardTitle || '',
          dashboardSubtitle: data.dashboardSubtitle || '',
          dashboardBody: data.dashboardBody || '',
          dashboardBtnText: data.dashboardBtnText || '',
          dashboardBulletsTitle: data.dashboardBulletsTitle || '',
          dashboardBullets: data.dashboardBullets || [],

          stepsTitle: data.stepsTitle || '',
          stepsSubtitle: data.stepsSubtitle || '',
          stepsBody: data.stepsBody || '',
          stepsBtnText: data.stepsBtnText || '',
          stepsBullets: data.stepsBullets || [],
          processSteps: data.processSteps || [],

          beneficiariesTitle: data.beneficiariesTitle || '',
          beneficiariesSubtitle: data.beneficiariesSubtitle || '',
          beneficiaries: data.beneficiaries || [],

          whyChooseTitle: data.whyChooseTitle || '',
          whyChooseSubtitle: data.whyChooseSubtitle || '',
          whyChoose: data.whyChoose || [],

          faqsTitle: data.faqsTitle || '',
          faqsSubtitle: data.faqsSubtitle || '',
          faqs: data.faqs || [],

          isPublished: data.isPublished !== false
        });
      }
    } catch (err) {
      toastError('Failed to load Zoho Advisory page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/zoho-advisory/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Zoho Advisory page content saved successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Are you sure you want to revert Zoho Advisory page options to default?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/zoho-advisory/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Restored default content successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/zoho-advisory/admin/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (res.data.success) {
        updateField(field, res.data.url);
        toastSuccess('Image uploaded and optimized successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload image');
    }
  };

  // Services Helpers
  const addService = () => {
    setForm(prev => ({
      ...prev,
      services: [...prev.services, { title: '', description: '', listLabel: '', bullets: [] }]
    }));
  };

  const removeService = (idx) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== idx)
    }));
  };

  const updateServiceField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.services];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, services: list };
    });
  };

  const addServiceBullet = (srvIdx) => {
    setForm(prev => {
      const list = [...prev.services];
      list[srvIdx].bullets = [...(list[srvIdx].bullets || []), ''];
      return { ...prev, services: list };
    });
  };

  const removeServiceBullet = (srvIdx, bulletIdx) => {
    setForm(prev => {
      const list = [...prev.services];
      list[srvIdx].bullets = list[srvIdx].bullets.filter((_, i) => i !== bulletIdx);
      return { ...prev, services: list };
    });
  };

  const updateServiceBulletField = (srvIdx, bulletIdx, val) => {
    setForm(prev => {
      const list = [...prev.services];
      const bullets = [...list[srvIdx].bullets];
      bullets[bulletIdx] = val;
      list[srvIdx].bullets = bullets;
      return { ...prev, services: list };
    });
  };

  // Dashboard Bullets
  const addDashboardBullet = () => {
    setForm(prev => ({
      ...prev,
      dashboardBullets: [...prev.dashboardBullets, '']
    }));
  };

  const removeDashboardBullet = (idx) => {
    setForm(prev => ({
      ...prev,
      dashboardBullets: prev.dashboardBullets.filter((_, i) => i !== idx)
    }));
  };

  const updateDashboardBulletField = (idx, val) => {
    setForm(prev => {
      const list = [...prev.dashboardBullets];
      list[idx] = val;
      return { ...prev, dashboardBullets: list };
    });
  };

  // Timeline & Steps Bullets
  const addStepsBullet = () => {
    setForm(prev => ({
      ...prev,
      stepsBullets: [...prev.stepsBullets, '']
    }));
  };

  const removeStepsBullet = (idx) => {
    setForm(prev => ({
      ...prev,
      stepsBullets: prev.stepsBullets.filter((_, i) => i !== idx)
    }));
  };

  const updateStepsBulletField = (idx, val) => {
    setForm(prev => {
      const list = [...prev.stepsBullets];
      list[idx] = val;
      return { ...prev, stepsBullets: list };
    });
  };

  const addProcessStep = () => {
    setForm(prev => ({
      ...prev,
      processSteps: [...prev.processSteps, { label: '', title: '', description: '' }]
    }));
  };

  const removeProcessStep = (idx) => {
    setForm(prev => ({
      ...prev,
      processSteps: prev.processSteps.filter((_, i) => i !== idx)
    }));
  };

  const updateProcessStepField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.processSteps];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, processSteps: list };
    });
  };

  // Beneficiaries
  const addBeneficiary = () => {
    setForm(prev => ({
      ...prev,
      beneficiaries: [...prev.beneficiaries, { title: '', body: '' }]
    }));
  };

  const removeBeneficiary = (idx) => {
    setForm(prev => ({
      ...prev,
      beneficiaries: prev.beneficiaries.filter((_, i) => i !== idx)
    }));
  };

  const updateBeneficiaryField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.beneficiaries];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, beneficiaries: list };
    });
  };

  // Why Choose
  const addWhyChoose = () => {
    setForm(prev => ({
      ...prev,
      whyChoose: [...prev.whyChoose, { title: '', body: '' }]
    }));
  };

  const removeWhyChoose = (idx) => {
    setForm(prev => ({
      ...prev,
      whyChoose: prev.whyChoose.filter((_, i) => i !== idx)
    }));
  };

  const updateWhyChooseField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.whyChoose];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, whyChoose: list };
    });
  };

  // FAQs
  const addFAQ = () => {
    setForm(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const removeFAQ = (idx) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== idx)
    }));
  };

  const updateFAQField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.faqs];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, faqs: list };
    });
  };

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="Zoho Advisory Page CMS"
        description="Edit dynamic headings, images, Zoho setup services checklists, dashboards trackers, process steps, beneficiaries, advantages, and page FAQs."
        icon={FaGlobe}
        actions={
          <div className="flex gap-3">
            <button
              onClick={handleRestoreDefaults}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
            >
              <FaUndo className="w-3.5 h-3.5" /> Revert Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto gap-2 mb-6 scrollbar-hide">
        {[
          { id: 'hero', name: 'Hero Banner' },
          { id: 'services', name: 'Implementation checklists' },
          { id: 'necessity', name: 'Zoho Necessity' },
          { id: 'dashboard', name: 'Dashboard Analytics' },
          { id: 'timeline', name: 'Onboarding blueprint' },
          { id: 'beneficiaries', name: 'Zoho Targets' },
          { id: 'choose', name: 'Choose BooksComply' },
          { id: 'faqs', name: 'Page FAQs' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 font-bold text-sm border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-white'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {/* Tab 1: Hero Banner */}
        {activeTab === 'hero' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Hero Header Layout</h2>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => updateField('isPublished', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Published
              </label>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Main Title</label>
              <input
                className="input-modern"
                value={form.heroTitle}
                onChange={(e) => updateField('heroTitle', e.target.value)}
                placeholder="Zoho Books, Payroll & Inventory Implementation..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Description Body</label>
              <textarea
                className="input-modern min-h-[100px]"
                value={form.heroBody}
                onChange={(e) => updateField('heroBody', e.target.value)}
                placeholder="Running your business becomes much easier..."
              />
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-b border-gray-100 dark:border-dark-700 py-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Primary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn1Text}
                  onChange={(e) => updateField('heroBtn1Text', e.target.value)}
                  placeholder="Start Zoho Consultation"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Secondary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn2Text}
                  onChange={(e) => updateField('heroBtn2Text', e.target.value)}
                  placeholder="View Zoho Service Plans"
                />
              </div>
            </div>

            <ServiceHeroExtrasEditor form={form} updateField={updateField} />

            {/* Image Upload */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Side Image</label>
              <div className="flex items-center gap-4">
                <input
                  className="input-modern flex-1"
                  value={form.heroImage}
                  onChange={(e) => updateField('heroImage', e.target.value)}
                  placeholder="Image URL path..."
                />
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                  <FaUpload className="w-3.5 h-3.5" /> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'heroImage')}
                    className="hidden"
                  />
                </label>
              </div>
              {form.heroImage && (
                <div className="mt-2 relative w-48 h-32 rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={form.heroImage.startsWith('http') ? form.heroImage : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.heroImage}`}
                    alt="Hero preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Implementation checklists */}
        {activeTab === 'services' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Zoho Modular Services</h2>
              <button
                onClick={addService}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Modular Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.servicesTitle}
                  onChange={(e) => updateField('servicesTitle', e.target.value)}
                  placeholder="Zoho Books, Zoho Payroll & Zoho Inventory Setup..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.servicesSubtitle}
                  onChange={(e) => updateField('servicesSubtitle', e.target.value)}
                  placeholder="Explore exactly how we deploy, migrate..."
                />
              </div>
            </div>

            <div className="space-y-6 pt-4">
              {form.services.map((service, srvIdx) => (
                <div key={srvIdx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-4 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeService(srvIdx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50 animate-fade-in"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Modular Card #{srvIdx + 1}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Card Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.title}
                        onChange={(e) => updateServiceField(srvIdx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-bold text-gray-400">List Column Label</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.listLabel}
                        onChange={(e) => updateServiceField(srvIdx, 'listLabel', e.target.value)}
                        placeholder="e.g. Implementation checklist"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Card Description</label>
                    <textarea
                      className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                      value={service.description}
                      onChange={(e) => updateServiceField(srvIdx, 'description', e.target.value)}
                    />
                  </div>

                  {/* Bullets Sub-Manager */}
                  <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-dark-700/50">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase font-bold text-gray-500">Deliverables / Bullets Checklist</label>
                      <button
                        onClick={() => addServiceBullet(srvIdx)}
                        className="flex items-center gap-1 text-[10px] text-primary-600 bg-primary-50 px-2 py-0.5 rounded font-bold"
                      >
                        <FaPlus className="w-2 h-2" /> Add Bullet
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(service.bullets || []).map((bullet, bulletIdx) => (
                        <div key={bulletIdx} className="flex gap-2 items-center">
                          <span className="text-xs font-bold text-gray-400">#{bulletIdx + 1}</span>
                          <input
                            className="input-modern text-sm py-1 px-3 flex-1"
                            value={bullet}
                            onChange={(e) => updateServiceBulletField(srvIdx, bulletIdx, e.target.value)}
                          />
                          <button
                            onClick={() => removeServiceBullet(srvIdx, bulletIdx)}
                            className="p-1.5 text-red-500 border rounded hover:bg-red-50"
                          >
                            <FaTrash className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 3: Zoho Necessity */}
        {activeTab === 'necessity' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Why Businesses Need Zoho Financial Advisory</h2>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Block Header Title</label>
              <input
                className="input-modern"
                value={form.whyAdvisoryTitle}
                onChange={(e) => updateField('whyAdvisoryTitle', e.target.value)}
                placeholder="Why Businesses Need Zoho Financial & Inventory Advisory..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Block Description Body</label>
              <textarea
                className="input-modern min-h-[120px]"
                value={form.whyAdvisoryBody}
                onChange={(e) => updateField('whyAdvisoryBody', e.target.value)}
                placeholder="BooksComply helps companies get the most out of Zoho Books..."
              />
            </div>
          </div>
        )}

        {/* Tab 4: Dashboard Analytics */}
        {activeTab === 'dashboard' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Zoho Dashboard & Reporting Section</h2>
              <button
                onClick={addDashboardBullet}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Tracked point
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Eyebrow Subtitle</label>
                <input
                  className="input-modern"
                  value={form.dashboardSubtitle}
                  onChange={(e) => updateField('dashboardSubtitle', e.target.value)}
                  placeholder="Zoho Dashboard & Reporting"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.dashboardTitle}
                  onChange={(e) => updateField('dashboardTitle', e.target.value)}
                  placeholder="Get Accurate Financial & Inventory Insights..."
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Dashboard Body Description</label>
              <textarea
                className="input-modern min-h-[100px]"
                value={form.dashboardBody}
                onChange={(e) => updateField('dashboardBody', e.target.value)}
                placeholder="Understanding your company performance becomes effortless..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-gray-100 dark:border-dark-700 pt-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Button Label</label>
                <input
                  className="input-modern"
                  value={form.dashboardBtnText}
                  onChange={(e) => updateField('dashboardBtnText', e.target.value)}
                  placeholder="Explore Zoho Dashboards"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Track Checklist Header Title</label>
                <input
                  className="input-modern"
                  value={form.dashboardBulletsTitle}
                  onChange={(e) => updateField('dashboardBulletsTitle', e.target.value)}
                  placeholder="What You Can Track with Zoho Dashboards"
                />
              </div>
            </div>

            {/* List checklist */}
            <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-dark-700">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block">Checklist Items</label>
              <div className="space-y-2">
                {form.dashboardBullets.map((bullet, idx) => (
                  <div key={idx} className="flex gap-2 items-center animate-fade-in">
                    <span className="text-sm text-gray-400 font-bold">#{idx + 1}</span>
                    <input
                      className="input-modern text-sm py-1.5 px-3 flex-1"
                      value={bullet}
                      onChange={(e) => updateDashboardBulletField(idx, e.target.value)}
                    />
                    <button
                      onClick={() => removeDashboardBullet(idx)}
                      className="p-2 text-red-500 border rounded-lg hover:bg-red-50"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab 5: Onboarding blueprint */}
        {activeTab === 'timeline' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Zoho Implementation Roadmap</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Eyebrow Subtitle</label>
                <input
                  className="input-modern"
                  value={form.stepsSubtitle}
                  onChange={(e) => updateField('stepsSubtitle', e.target.value)}
                  placeholder="How It Works"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.stepsTitle}
                  onChange={(e) => updateField('stepsTitle', e.target.value)}
                  placeholder="How Our Zoho Books, Payroll & Inventory Services Work"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Roadmap Body Paragraph</label>
              <textarea
                className="input-modern min-h-[80px]"
                value={form.stepsBody}
                onChange={(e) => updateField('stepsBody', e.target.value)}
                placeholder="Every Zoho rollout follows a structured blueprint..."
              />
            </div>

            <div className="space-y-1.5 border-t border-gray-100 dark:border-dark-700 pt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Action Button Label</label>
              <input
                className="input-modern"
                value={form.stepsBtnText}
                onChange={(e) => updateField('stepsBtnText', e.target.value)}
                placeholder="Schedule Zoho Implementation"
              />
            </div>

            {/* Steps checklist */}
            <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-dark-700">
              <div className="flex justify-between items-center">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Roadmap Checklist Bullets</label>
                <button
                  onClick={addStepsBullet}
                  className="flex items-center gap-1 text-xs text-primary-600 bg-primary-50 px-2.5 py-1 rounded-lg font-bold"
                >
                  <FaPlus className="w-2.5 h-2.5" /> Add Point
                </button>
              </div>

              <div className="space-y-2">
                {form.stepsBullets.map((bullet, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-sm text-gray-400 font-bold">#{idx + 1}</span>
                    <input
                      className="input-modern text-sm py-1.5 px-3 flex-1"
                      value={bullet}
                      onChange={(e) => updateStepsBulletField(idx, e.target.value)}
                    />
                    <button
                      onClick={() => removeStepsBullet(idx)}
                      className="p-2 text-red-500 border rounded-lg hover:bg-red-50"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Interactive Timeline steps list */}
            <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-dark-700">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Detailed Steps Timeline</label>
                <button
                  onClick={addProcessStep}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
                >
                  <FaPlus className="w-3 h-3" /> Add Timeline Step
                </button>
              </div>

              <div className="space-y-4">
                {form.processSteps.map((step, idx) => (
                  <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                    <button
                      onClick={() => removeProcessStep(idx)}
                      className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>

                    <h4 className="font-bold text-xs text-gray-800 dark:text-slate-200">Timeline Step #{idx + 1}</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Label Text</label>
                        <input
                          className="input-modern text-sm py-1.5 px-3"
                          value={step.label}
                          onChange={(e) => updateProcessStepField(idx, 'label', e.target.value)}
                          placeholder="e.g. Step 1"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Step Title</label>
                        <input
                          className="input-modern text-sm py-1.5 px-3"
                          value={step.title}
                          onChange={(e) => updateProcessStepField(idx, 'title', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Step Description</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={step.description}
                        onChange={(e) => updateProcessStepField(idx, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab 6: Zoho Targets */}
        {activeTab === 'beneficiaries' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Target Beneficiaries</h2>
              <button
                onClick={addBeneficiary}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Beneficiary Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Eyebrow Subtitle</label>
                <input
                  className="input-modern"
                  value={form.beneficiariesSubtitle}
                  onChange={(e) => updateField('beneficiariesSubtitle', e.target.value)}
                  placeholder="Who Benefits from These Zoho Services"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Beneficiaries Title</label>
                <input
                  className="input-modern"
                  value={form.beneficiariesTitle}
                  onChange={(e) => updateField('beneficiariesTitle', e.target.value)}
                  placeholder="Who Should Use Zoho Books, Payroll & Inventory..."
                />
              </div>
            </div>

            <div className="space-y-4 pt-4">
              {form.beneficiaries.map((benefit, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeBeneficiary(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Target #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Heading Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={benefit.title}
                        onChange={(e) => updateBeneficiaryField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Target Body Detail</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={benefit.body}
                        onChange={(e) => updateBeneficiaryField(idx, 'body', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 7: Choose BooksComply */}
        {activeTab === 'choose' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Choose BooksComply Reasons</h2>
              <button
                onClick={addWhyChoose}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Reason Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Eyebrow Subtitle</label>
                <input
                  className="input-modern"
                  value={form.whyChooseSubtitle}
                  onChange={(e) => updateField('whyChooseSubtitle', e.target.value)}
                  placeholder="Why Choose BooksComply"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Why Choose Title</label>
                <input
                  className="input-modern"
                  value={form.whyChooseTitle}
                  onChange={(e) => updateField('whyChooseTitle', e.target.value)}
                  placeholder="Why U.S. Businesses Choose BooksComply..."
                />
              </div>
            </div>

            <div className="space-y-4 pt-4">
              {form.whyChoose.map((item, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeWhyChoose(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Reason Card #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Card Header Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={item.title}
                        onChange={(e) => updateWhyChooseField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Card Description</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={item.body}
                        onChange={(e) => updateWhyChooseField(idx, 'body', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 8: Page FAQs */}
        {activeTab === 'faqs' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page FAQ Content</h2>
              <button
                onClick={addFAQ}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add FAQ Item
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Header Title</label>
                <input
                  className="input-modern"
                  value={form.faqsTitle}
                  onChange={(e) => updateField('faqsTitle', e.target.value)}
                  placeholder="Zoho Advisory FAQs..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Description</label>
                <input
                  className="input-modern"
                  value={form.faqsSubtitle}
                  onChange={(e) => updateField('faqsSubtitle', e.target.value)}
                  placeholder="Everything you need to know about Zoho..."
                />
              </div>
            </div>

            <div className="space-y-4 pt-4">
              {form.faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeFAQ(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">FAQ Item #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Question</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={faq.question}
                        onChange={(e) => updateFAQField(idx, 'question', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Answer</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[80px]"
                        value={faq.answer}
                        onChange={(e) => updateFAQField(idx, 'answer', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default ZohoAdvisoryCMS;
